/**
 * BroadcastChannel-based cross-tab query invalidation (Layer 1)
 *
 * Provides instant (~1ms) sync between tabs of the same user in the same browser.
 * Each tab has a unique ID so it can filter out its own broadcasts.
 */

import type { QueryClient } from '@tanstack/react-query'

/** Unique ID for this tab — used to skip self-broadcasts */
export const TAB_ID = crypto.randomUUID()

const CHANNEL_NAME = 'planmax-query-sync'

interface InvalidationMessage {
  type: 'invalidate'
  keys: readonly (readonly string[])[]
  tabId: string
  timestamp: number
}

/**
 * Broadcast query keys that were just invalidated locally so other tabs
 * of the same user can mirror the invalidation instantly.
 */
export function broadcastInvalidation(keys: readonly (readonly string[])[]) {
  try {
    const channel = new BroadcastChannel(CHANNEL_NAME)
    const message: InvalidationMessage = {
      type: 'invalidate',
      keys,
      tabId: TAB_ID,
      timestamp: Date.now(),
    }
    channel.postMessage(message)
    channel.close()
  } catch {
    // BroadcastChannel not supported or tab is closing — silently ignore
  }
}

/**
 * Start listening for invalidation messages from other tabs.
 * Returns a cleanup function to close the channel.
 */
export function createBroadcastListener(queryClient: QueryClient): () => void {
  try {
    const channel = new BroadcastChannel(CHANNEL_NAME)

    channel.onmessage = (event: MessageEvent<InvalidationMessage>) => {
      const { type, keys, tabId } = event.data

      // Skip messages from this tab
      if (type !== 'invalidate' || tabId === TAB_ID) return

      for (const key of keys) {
        queryClient.invalidateQueries({ queryKey: [...key] })
      }
    }

    return () => channel.close()
  } catch {
    // BroadcastChannel not supported — return noop
    return () => {}
  }
}
